/**
 * Data lineage for a saved dashboard: where each widget's numbers actually came from.
 *
 * The chain is real, not decorative — every widget in this app is produced by the same pipeline:
 *   the Excel dataset -> a tool call (query_data / aggregate) -> a rendered visual -> this dashboard.
 * Source chat titles come from the widget's own `sourceTitle`, captured when the dashboard was saved.
 */
export default function LineageModal({ dashboard, onClose }) {
  const widgets = dashboard.widgets || [];
  // The curator's summary widget has no single source chat (it synthesizes across all of them) — leave
  // it out of the source list rather than showing a phantom "Untitled chat".
  const sources = [...new Set(widgets.filter((w) => w.kind !== "summary").map((w) => w.sourceTitle || "Untitled chat"))];
  const charts = widgets.filter((w) => w.kind === "chart").length;
  const tables = widgets.filter((w) => w.kind === "table").length;

  const nodes = [
    { kind: "TABLE", icon: "▤", label: "Sales dataset", sub: `${widgets.length ? "3 sheets merged" : "no data"}` },
    { kind: "QUERY", icon: "<>", label: `${sources.length} conversation${sources.length === 1 ? "" : "s"}`, sub: "tool calls" },
    { kind: "VISUAL", icon: "▥", label: `${charts} chart${charts === 1 ? "" : "s"}, ${tables} table${tables === 1 ? "" : "s"}`, sub: "rendered" },
    { kind: "DASHBOARD", icon: "▩", label: dashboard.title, sub: "saved snapshot" },
  ];
  const edges = ["used_in", "generates", "displayed_in"];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="lineage-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lineage-head">
          <div>
            <div className="lineage-title">Data Lineage</div>
            <div className="lineage-sub">{dashboard.title}</div>
          </div>
          <div className="lineage-head-right">
            <span className="lineage-count">
              {nodes.length} nodes · {edges.length} edges
            </span>
            <button className="icon-btn" onClick={onClose} title="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="lineage-body">
          <div className="lineage-flow">
            {nodes.map((n, i) => (
              <div className="lineage-step" key={n.kind}>
                <div className={"lineage-node " + n.kind.toLowerCase()}>
                  <div className="lineage-node-icon">{n.icon}</div>
                  <div>
                    <div className="lineage-node-label">{n.label}</div>
                    <div className="lineage-node-kind">{n.kind}</div>
                  </div>
                </div>
                {i < edges.length && (
                  <div className="lineage-edge">
                    <span>{edges[i]}</span>
                    <div className="lineage-arrow" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {sources.length > 0 && (
            <div className="lineage-sources">
              <div className="lineage-sources-title">SOURCE CONVERSATIONS</div>
              <div className="source-list">
                {sources.map((s) => (
                  <span key={s} className="chip">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
